/**
 * Number cells: compact by default, exact on hover (title attribute).
 */
import {
  fmtAmount,
  fmtCompact,
  fmtExact,
  fmtPct,
  fmtPrice,
  fmtUsd,
  fmtUsdCents,
  fmtUsdExact,
  isZeroCents,
} from "@/lib/format";

interface NumProps {
  value: number | undefined | null;
  className?: string;
  /** color by sign */
  signed?: boolean;
}

function toneClass(v: number | undefined | null, signed: boolean): string {
  if (!signed || v === undefined || v === null || v === 0 || !Number.isFinite(v)) return "";
  return v > 0 ? "text-ok" : "text-danger";
}

export function Amount({
  value,
  symbol,
  className = "",
  signed = false,
  digits = 4,
}: NumProps & { symbol?: string; digits?: number }) {
  return (
    <span
      className={`num ${toneClass(value, signed)} ${className}`}
      title={value == null ? undefined : `${fmtExact(value, 8)}${symbol ? ` ${symbol}` : ""}`}
    >
      {fmtAmount(value, symbol, digits)}
    </span>
  );
}

export function Usd({
  value,
  className = "",
  signed = false,
  digits = 3,
  cents = false,
}: NumProps & {
  digits?: number;
  /** a result rounded to cents: within half a cent of zero it reads "$0.00", uncolored */
  cents?: boolean;
}) {
  const tone = cents && isZeroCents(value) ? "" : toneClass(value, signed);
  return (
    <span className={`num ${tone} ${className}`} title={value == null ? undefined : fmtUsdExact(value)}>
      {cents ? fmtUsdCents(value, digits) : fmtUsd(value, digits)}
    </span>
  );
}

export function Price({ value, currency = "$", className = "" }: NumProps & { currency?: string }) {
  return (
    <span
      className={`num ${className}`}
      title={value == null ? undefined : `${currency}${fmtExact(value, 10)}`}
    >
      {fmtPrice(value, currency)}
    </span>
  );
}

export function Pct({ value, className = "", signed = true, digits = 1 }: NumProps & { digits?: number }) {
  return (
    <span
      className={`num ${toneClass(value, signed)} ${className}`}
      title={value == null ? undefined : `${(value * 100).toString()}%`}
    >
      {fmtPct(value, { sign: signed, digits })}
    </span>
  );
}

export function Compact({
  value,
  className = "",
  signed = false,
  digits = 3,
}: NumProps & { digits?: number }) {
  return (
    <span
      className={`num ${toneClass(value, signed)} ${className}`}
      title={value == null ? undefined : fmtExact(value, 8)}
    >
      {fmtCompact(value, digits)}
    </span>
  );
}
